/**
 * @file
 * BEF Dropdown behavior.
 *
 * Handles open/close toggling, selected-count badge updates,
 * click-outside closing, Escape key support, scroll-position
 * preservation across AJAX refreshes, and debounced form submission
 * for the BEF Checkboxes/Radio Buttons Dropdown widget and plain
 * select filters.
 */
import { BREAKPOINTS } from '../../src/variables.ts';

((Drupal, once) => {
  'use strict';

  /**
   * Saved scroll position to restore after an AJAX refresh.
   */
  let savedScrollY: number | null = null;

  /**
   * Tracks which dropdowns were open before an AJAX refresh.
   * Keyed by the dropdown label text (stable across AJAX, unlike panel IDs
   * which Drupal regenerates with a random suffix on each refresh).
   */
  const openDropdownLabels = new Set<string>();

  /**
   * Debounce delay for batching filter changes (ms).
   */
  const DEBOUNCE_DELAY = 300;

  /**
   * Tracks pending input states across AJAX refreshes.
   * Keyed by `${name}\0${value}` → checked boolean.
   *
   * States accumulate as the user interacts with filters. They are
   * re-applied to the fresh DOM after every AJAX response, ensuring
   * that user selections made *during* an in-flight request are not
   * lost when Drupal rebuilds the form.
   */
  const pendingStates = new Map<string, boolean>();

  /**
   * Monotonically increasing counter bumped every time the form is
   * submitted. Used to decide whether new user input arrived after
   * the most recent submission and therefore requires another round-trip.
   */
  let submitGeneration = 0;

  /**
   * Generation at which each pending-state entry was recorded.
   * Entries added *after* the latest `triggerFormSubmit` call
   * (i.e. `stateGeneration > submitGeneration`) are "dirty" and
   * need a follow-up submission.
   */
  const stateGenerations = new Map<string, number>();

  /**
   * The generation counter at the time the current pending states
   * were last captured. Used in `attach()` to detect whether
   * any user changes were made after the most recent submission.
   */
  let latestInputGeneration = 0;

  /**
   * Debounce timer ID for form submission.
   */
  let debounceTimer: number | null = null;

  /**
   * Desktop breakpoint media query — kept in sync with the SCSS `lg` breakpoint.
   */
  const desktopMq = window.matchMedia(`(min-width: ${BREAKPOINTS.lg}px)`);

  /**
   * Tracks the label of the dropdown the user last interacted with
   * (changed a filter in). Persists across AJAX so the dropdown re-opens
   * after results reload, letting the user immediately select another filter.
   */
  let lastInteractedLabel: string | null = null;

  /**
   * Returns the stable label text for a dropdown element.
   */
  function getDropdownLabel(dropdown: HTMLElement): string {
    return dropdown.querySelector('.bef-dropdown__label')?.textContent?.trim() || '';
  }

  /**
   * Helper to create a unique key for an input's state.
   * @param input - The input element.
   */
  function makeStateKey(input: HTMLInputElement): string {
    return `${input.name}\0${input.value}`;
  }

  /**
   * Helper to split a state key into [name, value].
   * @param key - The state key.
   */
  function splitStateKey(key: string): [string, string] {
    const i = key.indexOf('\0');
    return [key.substring(0, i), key.substring(i + 1)];
  }

  /**
   * Determines whether a Views AJAX request is currently in flight for the
   * given form.
   *
   * Drupal marks its AJAX instance with `ajaxing === true` for the duration
   * of a request. We use this to serialize submissions (see
   * `triggerFormSubmit`) so responses can never arrive out of order.
   *
   * @param form - The exposed form element.
   *
   * @returns TRUE when at least one AJAX instance bound to the form is busy.
   */
  function isFormAjaxing(form: HTMLFormElement): boolean {
    const ajaxFn = (Drupal as unknown as Record<string, unknown>).ajax as
      { instances?: Array<{ element?: HTMLElement | false; ajaxing?: boolean } | null> } | undefined;

    if (!ajaxFn?.instances) return false;

    return ajaxFn.instances.some(
      (instance) =>
        !!instance &&
        instance.element instanceof HTMLElement &&
        form.contains(instance.element) &&
        instance.ajaxing === true
    );
  }

  /**
   * Triggers a form submission for the dropdown's form.
   *
   * Submissions are **serialized**: if a Views AJAX request is already in
   * flight, this call is a no-op. The user's changes remain recorded in
   * `pendingStates` (still "dirty"), and `attach()` re-submits them once the
   * in-flight response arrives. Serializing requests prevents concurrent
   * responses from completing out of order — an out-of-order stale response
   * could otherwise overwrite the DOM and leave a filter internally checked
   * while its active-filter chip is missing.
   *
   * `pendingStates` is intentionally **not** cleared here — entries are only
   * removed once they have been successfully re-applied to the fresh DOM
   * inside `attach()`.
   *
   * @param dropdown - The dropdown element.
   */
  function triggerFormSubmit(dropdown: HTMLElement): void {
    const form = dropdown.closest('form') as HTMLFormElement | null;
    if (!form) return;

    // Serialize AJAX requests: never start a new Views refresh while one is
    // already in flight. The pending states stay "dirty" and are resubmitted
    // by attach() once the in-flight response lands.
    if (isFormAjaxing(form)) {
      form.classList.add('views-exposed-form--loading');
      return;
    }

    // Bump the generation — any pending state recorded at or before
    // this generation is now "included in the submission".
    submitGeneration++;

    // Show the non-blocking loading indicator (progress bar + dimmed results).
    form.classList.add('views-exposed-form--loading');

    const submitBtn = form.querySelector<HTMLInputElement | HTMLButtonElement>(
      'input[type="submit"]:not([name="reset"]), button[type="submit"]:not([name="reset"])'
    );
    if (submitBtn) {
      savedScrollY = window.scrollY;
      submitBtn.click();
    } else if (form.requestSubmit) {
      savedScrollY = window.scrollY;
      form.requestSubmit();
    } else {
      savedScrollY = window.scrollY;
      form.submit();
    }
  }

  /**
   * Global click-outside handler registered once.
   * Closes any open desktop dropdown when clicking outside.
   * Keeps the dropdown open if the user recently interacted with it
   * (i.e. an AJAX refresh is pending) so it re-opens seamlessly.
   */
  function handleDocumentClick(event: MouseEvent): void {
    if (!desktopMq.matches) return;

    document.querySelectorAll<HTMLElement>('[data-bef-dropdown]').forEach((dropdown) => {
      const trigger = dropdown.querySelector<HTMLButtonElement>('.bef-dropdown__trigger');
      if (trigger && trigger.getAttribute('aria-expanded') === 'true' && !dropdown.contains(event.target as Node)) {
        // Keep the dropdown open if the user just interacted with it
        // and an AJAX refresh is pending — the dropdown will be restored
        // after the response arrives via lastInteractedLabel tracking.
        const label = getDropdownLabel(dropdown);
        if (label && label === lastInteractedLabel) {
          return;
        }
        trigger.setAttribute('aria-expanded', 'false');
        const panel = dropdown.querySelector<HTMLElement>('.bef-dropdown__panel');
        if (panel) {
          panel.classList.add('bef-dropdown__panel--hidden');
        }
        if (label) {
          openDropdownLabels.delete(label);
        }
      }
    });
  }

  // Register the global click-outside listener exactly once.
  document.addEventListener('click', handleDocumentClick);

  /**
   * Initializes BEF dropdown widgets.
   *
   * @description
   * Manages the interactive dropdown that wraps BEF checkboxes or radio
   * buttons to look and behave like a native select element. Keeps the
   * badge counter in sync with the number of checked inputs and prevents
   * the page from jumping after a Views AJAX refresh.
   */
  Drupal.behaviors.tpBefDropdown = {
    attach(context: Document | HTMLElement) {
      // Restore scroll position after an AJAX refresh.
      if (savedScrollY !== null) {
        window.scrollTo({ top: savedScrollY, behavior: 'instant' });
        savedScrollY = null;
      }

      // After every AJAX round-trip Drupal calls attachBehaviors.
      // Re-open the dropdown the user was interacting with. Since Drupal
      // regenerates element IDs on each AJAX refresh, we match by the
      // stable label text instead.
      if (lastInteractedLabel) {
        document.querySelectorAll<HTMLElement>('[data-bef-dropdown]').forEach((dropdown) => {
          if (getDropdownLabel(dropdown) === lastInteractedLabel) {
            const trigger = dropdown.querySelector<HTMLButtonElement>('.bef-dropdown__trigger');
            const panel = dropdown.querySelector<HTMLElement>('.bef-dropdown__panel');
            if (trigger && panel && trigger.getAttribute('aria-expanded') !== 'true') {
              trigger.setAttribute('aria-expanded', 'true');
              panel.classList.remove('bef-dropdown__panel--hidden');
              openDropdownLabels.add(lastInteractedLabel!);
            }
          }
        });
        lastInteractedLabel = null;
      }

      // Re-apply pending input states after AJAX refresh.
      // Every entry is re-applied to the fresh DOM so user selections
      // survive stale AJAX responses that rebuild the form.
      if (pendingStates.size > 0) {
        const root = context === document ? document.body : (context as HTMLElement);
        let hasDirty = false;

        pendingStates.forEach((checked, key) => {
          const [name, value] = splitStateKey(key);
          const input = root.querySelector<HTMLInputElement>(
            `input[name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`
          );
          if (input) {
            // Check whether the server-returned DOM already matches
            // the desired state. If it does, this entry is "clean"
            // and can be removed.
            if (input.checked === checked) {
              pendingStates.delete(key);
              stateGenerations.delete(key);
            } else {
              // Force the DOM to match the user's intent.
              input.checked = checked;

              // Is this entry "dirty" (added after the last submission)?
              const gen = stateGenerations.get(key) ?? 0;
              if (gen > submitGeneration) {
                hasDirty = true;
              }
            }
          }
        });

        // Only trigger a follow-up submission if the user made
        // changes AFTER the most recent submission (dirty states).
        // If all remaining states were already part of the last
        // request, the server response just hasn't arrived yet —
        // no need to re-submit.
        if (hasDirty) {
          const dropdown = root.querySelector('[data-bef-dropdown]') as HTMLElement | null;
          if (dropdown) {
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = window.setTimeout(() => {
              triggerFormSubmit(dropdown);
              if (Drupal.announce) {
                const msg = dropdown.dataset.i18nUpdating;
                if (msg) Drupal.announce(msg);
              }
            }, DEBOUNCE_DELAY);
          }
        }
      }

      const dropdowns = once('tp-bef-dropdown', '[data-bef-dropdown]', context);

      dropdowns.forEach((el) => {
        const dropdown = el as HTMLElement;
        const trigger = dropdown.querySelector('.bef-dropdown__trigger') as HTMLButtonElement;
        const panel = dropdown.querySelector('.bef-dropdown__panel') as HTMLElement;
        const badge = dropdown.querySelector('[data-bef-dropdown-badge]') as HTMLElement;

        if (!trigger || !panel) {
          return;
        }

        /**
         * Updates the badge with the count of selected inputs.
         */
        function updateBadge(): void {
          const checked = panel.querySelectorAll(
            'input[type="checkbox"]:checked:not([value=""]):not([value="All"]), input[type="radio"]:checked:not([value=""]):not([value="All"])'
          );
          const count = checked.length;
          const status = dropdown.querySelector<HTMLElement>('[data-bef-dropdown-status]');
          const anyEl = dropdown.querySelector<HTMLElement>('[data-bef-dropdown-status-any]');
          const selectedEl = dropdown.querySelector<HTMLElement>('[data-bef-dropdown-status-selected]');

          if (count > 0) {
            badge.textContent = String(count);
            badge.hidden = false;
            if (status) status.setAttribute('data-has-value', '');
            if (anyEl) anyEl.hidden = true;
            if (selectedEl) selectedEl.hidden = false;
          } else {
            badge.textContent = '';
            badge.hidden = true;
            if (status) status.removeAttribute('data-has-value');
            if (anyEl) anyEl.hidden = false;
            if (selectedEl) selectedEl.hidden = true;
          }
        }

        /**
         * Opens or closes the dropdown panel.
         *
         * @param open - Force open (true) or closed (false), or toggle if omitted.
         * @param focusPanel - Whether to move focus into the panel when opening.
         */
        function toggle(open?: boolean, focusPanel = false): void {
          const isOpen = open ?? trigger.getAttribute('aria-expanded') !== 'true';
          const label = getDropdownLabel(dropdown);

          trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
          panel.classList.toggle('bef-dropdown__panel--hidden', !isOpen);

          // Track open state for AJAX restore.
          if (label) {
            if (isOpen) {
              openDropdownLabels.add(label);
            } else {
              openDropdownLabels.delete(label);
            }
          }

          if (isOpen && focusPanel) {
            // Focus the first input inside the panel for keyboard users.
            const firstInput = panel.querySelector('input') as HTMLElement | null;
            firstInput?.focus();
          }
        }

        // Toggle on trigger click.
        trigger.addEventListener('click', () => toggle(undefined, true));

        // Close on Escape (desktop only – mobile panels stay open).
        dropdown.addEventListener('keydown', (event: KeyboardEvent) => {
          if (desktopMq.matches && event.key === 'Escape' && trigger.getAttribute('aria-expanded') === 'true') {
            toggle(false);
            trigger.focus();
          }
        });

        // Intercept input changes for debounced AJAX submission.
        panel.addEventListener('change', (event: Event) => {
          const input = event.target as HTMLInputElement;
          if (!input || (input.type !== 'checkbox' && input.type !== 'radio')) return;
          // Stop propagation so Views AJAX doesn't immediately submit.
          event.stopPropagation();
          // Store the pending state with its generation.
          const key = makeStateKey(input);
          pendingStates.set(key, input.checked);
          latestInputGeneration = submitGeneration + 1;
          stateGenerations.set(key, latestInputGeneration);
          // Update badge immediately for responsive UI.
          updateBadge();
          // Save scroll position.
          savedScrollY = window.scrollY;
          // Remember which dropdown the user is interacting with so it
          // re-opens after AJAX, even if click-outside fires during submit.
          const currentLabel = getDropdownLabel(dropdown);
          if (currentLabel) {
            lastInteractedLabel = currentLabel;
            openDropdownLabels.add(currentLabel);
          }
          // Show the loading overlay immediately so the user gets instant
          // visual feedback. Only the CSS class is added — `inert` is set
          // later by the MutationObserver in views-exposed-form.ts once AJAX
          // has started and form values have already been serialised.
          dropdown.closest('form')?.classList.add('views-exposed-form--loading');
          // Debounce the actual form submission.
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = window.setTimeout(() => {
            triggerFormSubmit(dropdown);
            if (Drupal.announce) {
              const msg = dropdown.dataset.i18nUpdating;
              if (msg) Drupal.announce(msg);
            }
          }, DEBOUNCE_DELAY);
        });

        // Set initial badge state.
        updateBadge();

        // Restore state: if this dropdown was open before AJAX, re-open it so
        // the user can immediately select another filter.
        const dropdownLabel = getDropdownLabel(dropdown);
        if (dropdownLabel && openDropdownLabels.has(dropdownLabel)) {
          toggle(true);
          if (desktopMq.matches) {
            // Focus the first input so the user can keep filtering.
            requestAnimationFrame(() => {
              const firstInput = panel.querySelector<HTMLElement>('input');
              firstInput?.focus();
            });
          }
        } else if (!desktopMq.matches) {
          // Mobile: open panels by default on first load.
          toggle(true);
        }
      });
    },

    detach(_context: Document | HTMLElement, _settings: unknown, trigger: string) {
      if (trigger === 'unload') {
        if (debounceTimer) {
          clearTimeout(debounceTimer);
          debounceTimer = null;
        }
      }
    },
  };

  // ---------------------------------------------------------------------------
  // Debounced select filter submission
  // ---------------------------------------------------------------------------

  /**
   * Tracks pending <select> states across AJAX refreshes.
   * Keyed by select element `name` → selected `value`.
   */
  const pendingSelectStates = new Map<string, string>();

  /**
   * Debounces plain `<select>` filter changes inside exposed forms.
   *
   * Drupal's AJAX system binds a jQuery `change` handler directly on each
   * `<select>`, causing an immediate AJAX call on every change. To give
   * the user time to adjust multiple filters we clone-replace each select
   * (stripping all jQuery/native listeners) and handle changes ourselves
   * using the same shared debounce timer as the BEF checkbox/radio panel.
   */
  Drupal.behaviors.tpBefSelectDebounce = {
    attach(context: Document | HTMLElement) {
      once('tp-bef-select-debounce', '.views-exposed-form', context).forEach((el) => {
        const form = el as HTMLFormElement;

        // Clone-replace every <select> to strip Drupal's jQuery AJAX
        // change handler. cloneNode(true) preserves the DOM state
        // (selected option, attributes, children) but removes all listeners.
        form.querySelectorAll<HTMLSelectElement>('select').forEach((select) => {
          const clone = select.cloneNode(true) as HTMLSelectElement;

          // Re-apply any pending select state that was captured before
          // this AJAX refresh replaced the DOM.
          const pending = pendingSelectStates.get(select.name);
          if (pending !== undefined) {
            clone.value = pending;
            // Clean up if the server already reflects this value.
            if (clone.value === pending) {
              pendingSelectStates.delete(select.name);
            }
          }

          select.replaceWith(clone);
        });

        // Listen for change events on the cloned selects.
        form.addEventListener('change', (event: Event) => {
          const target = event.target as HTMLElement;
          if (!(target instanceof HTMLSelectElement)) return;

          // Stop bubbling so no other handler triggers an immediate submit.
          event.stopPropagation();

          // Track the select state for re-application after AJAX refresh.
          pendingSelectStates.set(target.name, target.value);

          // Save scroll position for restoration after AJAX refresh.
          savedScrollY = window.scrollY;

          // Show the loading overlay immediately (CSS class only, no `inert`).
          form.classList.add('views-exposed-form--loading');

          // Debounce using the same shared timer as checkbox/radio changes.
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = window.setTimeout(() => {
            triggerFormSubmit(form);
            if (Drupal.announce) {
              const firstDropdown = form.querySelector<HTMLElement>('[data-bef-dropdown]');
              const msg = firstDropdown?.dataset.i18nUpdating;
              if (msg) Drupal.announce(msg);
            }
          }, DEBOUNCE_DELAY);
        });
      });
    },
  };
})(Drupal, once);
